/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';

import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Text from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import Popover from '@mui/material/Popover';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Select from '../common/Select';
import TextField from '@mui/material/TextField';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SettingsIcon from '@mui/icons-material/Settings';

import { SPEAKER_OPTIONS } from '../../constants';
import { useUserQuery } from '../../actions/UserActions';
import { useSupportedLanguages } from '../../utils/hooks';

export default function AdvancedSettingsMenu({
  numSpeakers,
  language,
  setSetting,
  disabled,
  outputFormat,
  name,
  assignedTo,
  transcribeMode,
  transcribeModeOptions,
  nameEdit = false,
}) {
  const supportedLanguages = useSupportedLanguages();
  const { t } = useTranslation();
  const { user } = useUserQuery();
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const languageOptions = useMemo(() => {
    if (!user) {
      return [{ label: t('Automatisk'), value: 'auto' }];
    }

    return Object.entries(supportedLanguages)
      .filter(
        ([langCode]) =>
          user?.workspace?.languages?.includes(langCode) || langCode === 'auto'
      )
      .map(([langCode, label]) => ({
        label,
        value: langCode,
      }));
  }, [user]);

  useEffect(() => {
    if (!assignedTo) {
      setSetting('assignedTo', user.id);
    }
    if (user?.workspace?.transcribeModes?.length === 1) {
      setSetting('transcribeMode', user.workspace.transcribeModes[0].id);
    }
    if (user?.workspace?.output_format) {
      setSetting('outputFormat', user?.workspace?.output_format);
    }
  }, [user, assignedTo]);

  return (
    <React.Fragment>
      <Button
        css={css`
          display: flex;
          align-items: center;
          cursor: pointer;
        `}
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        color={open ? 'primary' : 'text'}
        startIcon={<SettingsIcon />}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          backgroundColor: open ? '#DCE8FF' : 'transparent',
          borderRadius: '15rem',
        }}
      >
        <Text fontWeight="600">{t('Avancerat')}</Text>
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div
          css={css`
            padding: 1rem;
            display: flex;
            flex-direction: column;
            gap: 1rem;
            width: 300px;
          `}
        >
          {nameEdit && (
            <div>
              <Text variant="subtitle2" fontWeight="600">
                {t('Namn')}
              </Text>
              <TextField
                value={name}
                onChange={(e) => setSetting('name', e.target.value)}
                size="small"
                css={css`
                  width: 100%;
                `}
                disabled={disabled}
              />
            </div>
          )}
          <div>
            <Text variant="subtitle2" fontWeight="600">
              {t('Språk')}
            </Text>
            <Select
              value={language}
              onChange={(e) => setSetting('language', e.target.value)}
              size="small"
              onClick={(e) => e.stopPropagation()}
              options={languageOptions}
              css={css`
                width: 100%;
              `}
              disabled={languageOptions.length === 1 || disabled}
            />
          </div>
          <div>
            <Text variant="subtitle2" fontWeight="600">
              {t('Antal talare')}
            </Text>
            <Select
              value={numSpeakers}
              onChange={(e) => setSetting('numSpeakers', e.target.value)}
              size="small"
              onClick={(e) => e.stopPropagation()}
              options={SPEAKER_OPTIONS}
              css={css`
                width: 100%;
              `}
              disabled={disabled}
            />
          </div>
          {transcribeModeOptions?.length > 1 && (
            <div>
              <Text variant="subtitle2" fontWeight="600">
                {t('Kommunkoll')}
              </Text>
              <Select
                value={transcribeMode}
                onChange={(e) => setSetting('transcribeMode', e.target.value)}
                size="small"
                onClick={(e) => e.stopPropagation()}
                options={transcribeModeOptions}
                disabled={disabled}
                css={css`
                  width: 100%;
                  margin-top: 0.25rem;
                `}
              />
            </div>
          )}
          <FormControl
            css={css`
              width: 100%;
            `}
            disabled={disabled}
          >
            <FormLabel focused={false} sx={{ fontWeight: 600 }}>
              {t('Formatering')}
            </FormLabel>
            <RadioGroup
              value={outputFormat}
              onChange={(e) => setSetting('outputFormat', e.target.value)}
            >
              <FormControlLabel
                value="subtitle"
                control={<Radio />}
                label={
                  <div>
                    <Text variant="body" sx={{ display: 'inline' }}>
                      {t('Undertextning')}
                    </Text>
                    <Tooltip
                      title={t(
                        'Längden anpassad för att visning som undertexter'
                      )}
                    >
                      <HelpOutlineIcon
                        sx={{
                          fontSize: '1rem',
                          verticalAlign: 'middle',
                          marginLeft: '0.5rem',
                        }}
                      />
                    </Tooltip>
                  </div>
                }
              />
              <FormControlLabel
                value="transcript"
                control={<Radio />}
                label={
                  <div>
                    <Text variant="body" sx={{ display: 'inline' }}>
                      {t('Transkribering')}
                    </Text>
                    <Tooltip
                      title={t(
                        'Längre segment som alltid bryts vid talarskifte'
                      )}
                    >
                      <HelpOutlineIcon
                        sx={{
                          fontSize: '1rem',
                          verticalAlign: 'middle',
                          marginLeft: '0.5rem',
                        }}
                      />
                    </Tooltip>
                  </div>
                }
              />
            </RadioGroup>
          </FormControl>
        </div>
      </Popover>
    </React.Fragment>
  );
}
